import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { Router } from '@angular/router';

import { ConfigService } from '../core/services/config.service';
import { IconComponent } from '../shared/components/icon.component';
import { TranslatePipe } from '../shared/pipes/translate.pipe';

/** Desktop footer: brand + socials + copyright, quick links, help, and the Sonic credit. */
@Component({
  selector: 'app-desktop-footer',
  standalone: true,
  imports: [NgFor, NgIf, IconComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: block; flex-shrink: 0; background: var(--brand-ink); color: #fff; margin-top: 70px; padding-top: 54px; }
    .in { max-width: 1280px; margin: 0 auto; padding: 0 32px; }
    .grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 38px; padding-bottom: 40px; }
    @media (max-width: 1100px) { .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    .brand { display: flex; align-items: center; gap: 11px; margin-bottom: 14px; }
    .mark { width: 44px; height: 44px; border-radius: 12px; overflow: hidden; display: block; background: rgba(255,255,255,0.08); }
    .mark img { width: 100%; height: 100%; object-fit: contain; display: block; }
    .bn { font-family: var(--brand-display); font-weight: 750; font-size: 18px; }
    .tag { margin: 0; font-size: 13.5px; line-height: 1.6; color: rgba(255,255,255,0.6); max-width: 280px; }
    .soc { display: flex; gap: 9px; margin-top: 18px; flex-wrap: wrap; }
    .soc a { width: 38px; height: 38px; border-radius: 10px; background: rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; text-decoration: none; }
    .soc a:hover { background: rgba(255,255,255,0.18); }
    .copy { margin-top: 18px; font-size: 12.5px; color: rgba(255,255,255,0.45); }
    .h { font-size: 12px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-bottom: 10px; }
    .lnk { display: block; width: 100%; text-align: start; font-size: 14px; color: rgba(255,255,255,0.82); text-decoration: none; padding: 6px 0; cursor: pointer;
      background: none; border: none; font-family: var(--fr-font); }
    .lnk:hover { color: #fff; }
    .tel { display: inline-block; color: #fff; font-weight: 750; font-size: 19px; text-decoration: none; padding: 4px 0 2px; }
    .hrs { font-size: 12.5px; color: rgba(255,255,255,0.5); margin-bottom: 8px; }
    .bar { border-top: 1px solid rgba(255,255,255,0.1); padding: 20px 0 26px; display: flex; flex-wrap: wrap; gap: 14px; align-items: center; justify-content: center;
      font-size: 12.5px; color: rgba(255,255,255,0.45); }
    .pow { display: inline-flex; align-items: center; gap: 8px; }
    .ftxt { flex-basis: 100%; margin: 0; text-align: center; font-size: 13px; line-height: 1.6; color: rgba(255,255,255,0.62); white-space: pre-line; }
    .pow img { height: 20px; width: auto; display: block; }
  `],
  template: `<div class="in">
      <div class="grid">
        <div>
          <div class="brand">
            <span class="mark"><img *ngIf="config.brandLogo" [src]="config.brandLogo" alt="" onerror="this.style.display='none'"></span>
            <span class="bn">{{ config.brandName }}</span>
          </div>
          <p class="tag" *ngIf="config.brandTagline">{{ config.brandTagline }}                    <span class="tag" *ngIf="config.footerText"> -{{ config.footerText }}</span></p>

          
          <div class="soc" *ngIf="config.social.length">
            <a *ngFor="let s of config.social" [href]="s.url" target="_blank" rel="noopener" [attr.aria-label]="s.label">
              <app-icon [name]="s.icon" [size]="18" color="rgba(255,255,255,0.85)"></app-icon>
            </a>
          </div>
          <div class="copy">© {{ year }} {{ config.brandName }}. {{ 'All rights reserved.' | t }}</div>
        </div>

        <div>
          <div class="h">{{ 'Order' | t }}</div>
          <button class="lnk" (click)="go('menu')">{{ 'Full menu' | t }}</button>
          <button class="lnk" *ngIf="config.reservationsEnabled" (click)="go('reservation')">{{ 'Book a table' | t }}</button>
          <button class="lnk" (click)="go('orders')">{{ 'Track an order' | t }}</button>
          <button class="lnk" (click)="go('account')">{{ 'My account' | t }}</button>
        </div>

        <div>
          <div class="h">{{ 'Help' | t }}</div>
          <a *ngIf="config.brandHotline" class="tel fr-num fr-ltr" [href]="'tel:' + config.brandHotline">{{ config.brandHotline }}</a>
          <div class="hrs" *ngIf="config.hotlineHours">{{ config.hotlineHours | t }}</div>
          <button class="lnk" (click)="goAccountTab('help')">{{ 'Help & support' | t }}</button>
          <button class="lnk" *ngIf="config.legal.terms" (click)="go('account')">{{ 'Terms & conditions' | t }}</button>
          <button class="lnk" *ngIf="config.legal.privacy" (click)="go('account')">{{ 'Privacy policy' | t }}</button>
        </div>
      </div>

      <div class="bar">
        <span class="pow">{{ 'Powered by' | t }}
          <a href="https://www.sonic-pos.com/" target="_blank" rel="noopener" style="display:inline-flex"><img src="assets/sonic.png" alt="Sonic" onerror="this.style.display='none'"></a>
        </span>
      </div>
    </div>`,
})
export class DesktopFooterComponent {
  readonly year = new Date().getFullYear();

  constructor(public config: ConfigService, private router: Router) {}

  go(screen: string): void { this.router.navigate(['/', screen]); }
  /** Open the account page on a given tab (e.g. Help & support). */
  goAccountTab(tab: string): void { this.router.navigate(['/account'], { queryParams: { tab } }); }
}
